import { io, Socket } from 'socket.io-client';
import { SOCKET_EVENTS } from 'constants/game';
import { emitSessionExpired, getToken, markIntentionalTableLeave } from 'lib/api';

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || 'http://localhost:5002';

let socket: Socket | null = null;

export interface LeftTableResultPayload {
  success?: boolean;
  message?: string;
  walletBalance?: number;
  cashOut?: number;
  remainingStack?: number;
}

let leftTableResultHandler: ((payload: LeftTableResultPayload) => void) | null = null;

/** Survives table unmount — wallet refresh after leave_table ACK. */
export function setLeftTableResultHandler(
  handler: ((payload: LeftTableResultPayload) => void) | null
): void {
  leftTableResultHandler = handler;
}

function wirePersistentHandlers(sock: Socket): void {
  const tagged = sock as Socket & { __tpPersistentHandlers?: boolean };
  if (tagged.__tpPersistentHandlers) return;
  tagged.__tpPersistentHandlers = true;

  sock.on(SOCKET_EVENTS.SERVER.SESSION_KICKED, () => {
    emitSessionExpired({ force: true });
  });

  sock.on('connect_error', (err: Error) => {
    const msg = String(err?.message || '').toLowerCase();
    if (msg.includes('unauthorized') || msg.includes('invalid')) {
      emitSessionExpired({ force: true });
    }
  });

  sock.on(SOCKET_EVENTS.SERVER.ACTION_RESULT, (payload: LeftTableResultPayload) => {
    if (payload?.success && payload.message === 'Left table') {
      markIntentionalTableLeave();
      leftTableResultHandler?.(payload);
    }
  });
}

export function connectGameSocket(): Socket | null {
  const token = getToken();
  if (!token) return null;

  if (socket) {
    socket.auth = { token };
    wirePersistentHandlers(socket);
    if (!socket.connected) socket.connect();
    return socket;
  }

  socket = io(`${SOCKET_URL}/game`, {
    path: '/socket.io',
    auth: { token },
    transports: ['websocket', 'polling'],
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 500,
    reconnectionDelayMax: 2500,
    timeout: 10000,
  });

  wirePersistentHandlers(socket);

  return socket;
}

export function disconnectGameSocket(): void {
  if (socket) {
    socket.removeAllListeners();
    socket.disconnect();
    socket = null;
  }
}

export function getGameSocket(): Socket | null {
  return socket;
}

export { SOCKET_EVENTS };
